import { useState } from "react";
import { countNonEmptyLines } from "@link-factory/shared/CountNonEmptyLines";
import { siteParameterListMaxNonEmptyLines } from "@link-factory/shared/SiteParameterListMaxNonEmptyLines";
import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { logger } from "../../observability/LoggerFactory";
import type { CopyTarget, UpdateParameter } from "./UpdateParamsDialog";
import {
  CLOSED_UPDATE_STATE,
  TRANSACTION_ABORTED_MESSAGE,
  buildUpdateRequest,
  closeRequested,
  copyTargetClipboardText,
  failed,
  lineFormatFor,
  openedDialog,
  readRefusalReason,
  refusalMessage,
  returnedToInput,
  sendingStarted,
  succeeded,
  withCopyStatus,
  withParameterToggled,
  withRawData,
} from "./useUpdateSiteParams.logic";
import type { UpdateSiteParamsResponse, UpdateSiteParamsState } from "./useUpdateSiteParams.logic";

export interface UseUpdateSiteParamsResult {
  state: UpdateSiteParamsState;
  lineFormat: string;
  nonEmptyLineCount: number;
  maxLines: number;
  open: () => void;
  toggleParameter: (parameter: UpdateParameter) => void;
  changeRawData: (rawData: string) => void;
  submit: () => void;
  cancel: () => void;
  returnToInput: () => void;
  discard: () => void;
  closeResult: () => void;
  /** «Скопировать» у одного из двух списков итога (SiteList.md «Копирование списка итога»). */
  copy: (target: CopyTarget) => void;
}

/**
 * Диалог обновления параметров сайтов: автомат раздела «Переходы» экрана
 * SiteList над запросом SITE-24. `onResultClosed` — перезапрос списка SITE-02
 * при закрытии итога: записанные значения видны в таблице.
 */
export function useUpdateSiteParams(onResultClosed: () => void): UseUpdateSiteParamsResult {
  const [state, setState] = useState<UpdateSiteParamsState>(CLOSED_UPDATE_STATE);

  const submit = () => {
    const body = buildUpdateRequest(state);
    setState(sendingStarted);
    void (async () => {
      try {
        const response = await fetchWithRefresh("/api/sites/params/update", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (response.ok) {
          const summary = (await response.json()) as UpdateSiteParamsResponse;
          setState((current) => succeeded(current, summary));
        } else {
          const reason = await readRefusalReason(response);
          logger.error({ status: response.status, reason }, "Site parameters update refused");
          setState((current) => failed(current, refusalMessage(response.status, reason)));
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Unknown error";
        logger.error({ err: message }, "Site parameters update failed");
        setState((current) => failed(current, TRANSACTION_ABORTED_MESSAGE));
      }
    })();
  };

  // Буфер обмена как в LogsSection: navigator.clipboard.writeText напрямую;
  // отказ или недоступный API — статус кнопки "failed", список остаётся на экране.
  const copy = (target: CopyTarget) => {
    if (state.summary === null) {
      return;
    }
    const text = copyTargetClipboardText(state.summary, target);
    void (async () => {
      try {
        if (navigator.clipboard === undefined) {
          throw new Error("Clipboard API unavailable");
        }
        await navigator.clipboard.writeText(text);
        setState((current) => withCopyStatus(current, target, "copied"));
      } catch (error: unknown) {
        logger.error({ err: error instanceof Error ? error.message : "Unknown error" }, "Clipboard copy failed");
        setState((current) => withCopyStatus(current, target, "failed"));
      }
    })();
  };

  const closeResult = () => {
    setState(CLOSED_UPDATE_STATE);
    onResultClosed();
  };

  return {
    state,
    lineFormat: lineFormatFor(state.selectedParameters),
    nonEmptyLineCount: countNonEmptyLines(state.rawData),
    maxLines: siteParameterListMaxNonEmptyLines,
    open: () => setState(openedDialog()),
    toggleParameter: (parameter) => setState((current) => withParameterToggled(current, parameter)),
    changeRawData: (rawData) => setState((current) => withRawData(current, rawData)),
    submit,
    cancel: () => setState(closeRequested),
    returnToInput: () => setState(returnedToInput),
    discard: () => setState(CLOSED_UPDATE_STATE),
    closeResult,
    copy,
  };
}
